import { useState, forwardRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Instagram, Facebook, Loader2, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";

const TikTokIcon = forwardRef<SVGSVGElement, { className?: string }>(({ className }, ref) => (
  <svg ref={ref} className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.72a8.19 8.19 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.15z"/>
  </svg>
));
TikTokIcon.displayName = "TikTokIcon";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const { t } = useLanguage();
  const navigate = useNavigate();

  const socialLinks = [
    { icon: Instagram, label: "Instagram", url: "https://www.instagram.com/bayt_altib?igsh=MWliZ25ucm91dWN0cQ==" },
    { icon: Facebook, label: "Facebook", url: "https://www.facebook.com/bayt.altib/" },
    { icon: TikTokIcon, label: "TikTok", url: "https://www.tiktok.com/@bait.al_teeb" },
    { icon: MessageCircle, label: "WhatsApp", url: "https://api.whatsapp.com/send/?phone=201004836927&text=I%27m+interested+in+Bait+Al-Teeb+fragrances.+Can+you+help+me+understand+more%3F&type=phone_number&app_absent=0" },
  ];

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsSubscribing(true);
    try {
      const { error } = await supabase.from("newsletter_subscriptions").insert({ email });
      if (error) {
        if (error.code === "23505") {
          toast.info(t.footer.alreadySubscribed);
        } else {
          throw error;
        }
      } else {
        toast.success(t.footer.subscribeSuccess);
        setEmail("");
      }
    } catch (error: any) {
      toast.error("Failed to subscribe: " + error.message);
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <footer className="bg-card border-t border-border">
      <div className="container-luxury py-10 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10 md:gap-12">
          {/* Brand */}
          <div className="sm:col-span-2 md:col-span-1 text-center md:text-start">
            <h3 className="text-2xl font-serif text-gradient-gold mb-2 arabic">بَيْتُ الطِّيبِ</h3>
            <p className="text-xs tracking-[0.2em] text-muted-foreground uppercase mb-4">Bait Al-Teeb</p>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs mx-auto md:mx-0">{t.footer.brandDesc}</p>
          </div>

          {/* Quick Links */}
          <div className="text-center sm:text-start">
            <h4 className="font-serif text-lg mb-4">{t.footer.explore}</h4>
            <ul className="space-y-3">
              {t.footer.exploreLinks.map((item: string, i: number) => {
                const explorePaths = ["/collection", "/collection", "/#best-sellers", "/collection"];
                const path = explorePaths[i];
                const isHashLink = path.includes("#");
                return (
                  <li key={item}>
                    {isHashLink ? (
                      <button
                        onClick={() => {
                          navigate("/");
                          setTimeout(() => {
                            const el = document.getElementById(path.split("#")[1]);
                            el?.scrollIntoView({ behavior: "smooth" });
                          }, 100);
                        }}
                        className="text-muted-foreground hover:text-primary transition-colors text-sm"
                      >
                        {item}
                      </button>
                    ) : (
                      <Link to={path} className="text-muted-foreground hover:text-primary transition-colors text-sm">{item}</Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Support */}
          <div className="text-center sm:text-start">
            <h4 className="font-serif text-lg mb-4">{t.footer.support}</h4>
            <ul className="space-y-3">
              {t.footer.supportLinks.map((item: string, i: number) => {
                const supportPaths = ["/contact", "/track-order", "/returns", "/faq"];
                return (
                  <li key={item}>
                    <Link to={supportPaths[i]} className="text-muted-foreground hover:text-primary transition-colors text-sm">{item}</Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Newsletter */}
          <div className="sm:col-span-2 md:col-span-1 text-center sm:text-start">
            <h4 className="font-serif text-lg mb-4">{t.footer.stayConnected}</h4>
            <p className="text-muted-foreground text-sm mb-4">{t.footer.subscribeDesc}</p>
            <form onSubmit={handleSubscribe} className="flex gap-2 max-w-sm mx-auto sm:mx-0">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t.footer.emailPlaceholder}
                className="flex-1 min-w-0 bg-muted border border-border rounded-sm px-4 py-2 text-sm focus:outline-none focus:border-primary transition-colors"
                required
              />
              <button type="submit" disabled={isSubscribing} className="px-4 py-2 bg-primary text-primary-foreground rounded-sm text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 shrink-0">
                {isSubscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : t.footer.joinBtn}
              </button>
            </form>

            <div className="flex gap-4 mt-6 justify-center sm:justify-start">
              {socialLinks.map((social) => (
                <a key={social.label} href={social.url} target="_blank" rel="noopener noreferrer" aria-label={social.label} className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-colors">
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="divider-gold mt-10 md:mt-12 mb-6 md:mb-8" />
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground text-center">
          <p>{t.footer.copyright}</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-primary transition-colors">{t.footer.privacy}</Link>
            <Link to="/terms" className="hover:text-primary transition-colors">{t.footer.terms}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
